import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { usePublishRevision } from '@/features/exam-revision/hooks/usePublishRevision';
import { useRevisionGrid } from '@/features/exam-revision/hooks/useRevisionGrid';
import type { RevisionGridItem } from '@/features/exam-revision/types/revision';
import { PublishModal } from '@/features/exam-revision/ui/organisms/PublishModal';
import { RevisionListTemplate } from '@/features/exam-revision/ui/templates/RevisionListTemplate';
import type { PublishBotExamViewModel } from '@/shared/api/generated/types/PublishBotExamViewModel';

export const Route = createFileRoute('/_authenticated/exam/revision/')({
  component: RevisionListPage,
});

export function RevisionListPage() {
  const navigate = useNavigate();
  const {
    items,
    filters,
    setFilters,
    applyFilters,
    resetFilters,
    totalItems,
    hasNextPage,
    currentPage,
    pageSize,
    setPage,
    sorting,
    setSorting,
    isLoading,
    isError,
    refetch,
    resetKey,
  } = useRevisionGrid();

  const handleTranslate = (item: RevisionGridItem) => {
    void navigate({
      to: '/exam/revision/$examId/languages' as never,
      params: { examId: String(item.id) } as never,
    });
  };

  const handleEdit = (item: RevisionGridItem) => {
    void navigate({
      to: '/exam/revision/$examId/review' as never,
      params: { examId: String(item.id) } as never,
    });
  };

  const handleView = (item: RevisionGridItem) => {
    void navigate({
      to: '/exam/revision/$examId/visualize' as never,
      params: { examId: String(item.id) } as never,
    });
  };

  const [approvingItem, setApprovingItem] = useState<RevisionGridItem | null>(null);
  const approvingBotExamId = approvingItem?.id ?? 0;

  const { show, isLoadingShow, publish, isPublishing } = usePublishRevision(
    approvingBotExamId,
    approvingItem !== null,
  );

  const handleApprove = (item: RevisionGridItem) => {
    setApprovingItem(item);
  };

  const handleConfirmApprove = (data: PublishBotExamViewModel) => {
    publish(data, { onSuccess: () => setApprovingItem(null) });
  };

  return (
    <>
      <RevisionListTemplate
        items={items}
        filters={filters}
        onFilterChange={patch => setFilters({ ...filters, ...patch })}
        onSearch={applyFilters}
        onClear={resetFilters}
        totalItems={totalItems}
        hasNextPage={hasNextPage}
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={setPage}
        sorting={sorting}
        onSortingChange={setSorting}
        onApprove={handleApprove}
        onTranslate={handleTranslate}
        onEdit={handleEdit}
        onView={handleView}
        isLoading={isLoading}
        isError={isError}
        onRetry={refetch}
        resetKey={resetKey}
      />
      <PublishModal
        open={approvingItem !== null}
        botExamId={approvingBotExamId}
        show={show}
        isLoadingShow={isLoadingShow}
        isPublishing={isPublishing}
        onClose={() => setApprovingItem(null)}
        onConfirm={handleConfirmApprove}
      />
    </>
  );
}
